import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Power } from "lucide-react";
import { toast } from "sonner";
import { unitSuffix } from "@/lib/pricing";

const empty = { category_id: "", title: "", description: "", price: 0, pricing_unit: "flat", duration_minutes: 60, image_url: "", location: "" };

export default function ProviderListings() {
  const { provider } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const approved = provider?.kyc?.status === "approved";

  const load = () => {
    api.get("/provider/services").then(({ data }) => setItems(data));
  };

  useEffect(() => {
    load();
    api.get("/categories").then(({ data }) => setCategories(data));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      category_id: s.category_id,
      title: s.title,
      description: s.description,
      price: s.price,
      pricing_unit: s.pricing_unit || "flat",
      duration_minutes: s.duration_minutes,
      image_url: s.image_url || "",
      location: s.location || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/services/${editing.id}`, form);
        toast.success("Listing updated");
      } else {
        await api.post("/services", { ...form, price: parseFloat(form.price), duration_minutes: parseInt(form.duration_minutes) || 60 });
        toast.success("Listing created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/services/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/services/${s.id}`, { is_active: !s.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="provider-listings-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">My listings</h1>
          <p className="mt-1 text-sm text-muted-foreground">Create, edit, and toggle your service offerings.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              disabled={!approved}
              onClick={openCreate}
              className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
              data-testid="new-listing-btn"
            >
              <Plus size={16} className="mr-1.5" /> New listing
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit listing" : "Create listing"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="listing-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="listing-title" />
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="listing-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Price (₹)</Label>
                  <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="listing-price" />
                </div>
                <div className="space-y-1">
                  <Label>Pricing unit</Label>
                  <Select value={form.pricing_unit} onValueChange={(v) => setForm({ ...form, pricing_unit: v })}>
                    <SelectTrigger data-testid="listing-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat / job</SelectItem>
                      <SelectItem value="hour">Per hour</SelectItem>
                      <SelectItem value="day">Per day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Duration (min)</Label>
                <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="listing-duration" />
              </div>
              <div className="space-y-1">
                <Label>Image URL</Label>
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." data-testid="listing-image" />
              </div>
              <div className="space-y-1">
                <Label>Service area</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City" data-testid="listing-location" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90" data-testid="save-listing">
                {editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!approved && (
        <div className="rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-4 text-sm">
          You must complete KYC and get approval before adding listings.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
            No listings yet.
          </div>
        )}
        {items.map((s) => (
          <div key={s.id} className="overflow-hidden rounded-2xl border bg-white" data-testid={`listing-${s.id}`}>
            <div className="relative aspect-[16/10]">
              <img src={s.image_url || s.category?.image_url} alt={s.title} className="h-full w-full object-cover" />
              {!s.is_active && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-xs font-semibold text-white">Paused</div>
              )}
            </div>
            <div className="p-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">{s.category?.name}</div>
              <div className="font-heading text-base font-bold">{s.title}</div>
              <div className="mt-1 flex items-center justify-between">
                <div className="font-heading text-lg font-extrabold">
                  ₹{s.price}
                  {s.pricing_unit && s.pricing_unit !== "flat" && (
                    <span className="ml-1 text-xs font-semibold text-muted-foreground">{unitSuffix(s.pricing_unit)}</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">{s.duration_minutes} min</div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(s)} data-testid={`edit-${s.id}`}><Pencil size={12} className="mr-1" />Edit</Button>
                <Button variant="outline" size="sm" onClick={() => toggle(s)} data-testid={`toggle-${s.id}`}><Power size={12} className="mr-1" />{s.is_active ? "Pause" : "Activate"}</Button>
                <Button variant="outline" size="sm" onClick={() => del(s.id)} data-testid={`delete-${s.id}`}><Trash2 size={12} className="mr-1 text-brand-orange" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
